import { Subject, from, combineLatest } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { Data } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { platform } from '../../../../platform/platform';
import { Inspection, InspectionLocation, InspectionUpdateLocation } from '../../../_objects/inspection';
import { InspectionImage, InspectionImageListItem } from '../../../_objects/inspection-image';
import { SubjectLocation, SubjectModel } from '../../../_objects/subject-model';
import { UtilsService } from '../../../_services/_utils.service';
import { CommonSnackBarService } from '../../../_services/common-snack-bar.service';
import { extractFilesFromSystemEntries } from '../../../_services/image-upload/file-system-entries-extract.service';
import { InspectionService } from '../../../_services/http-api/inspection.service';
import { ImageUploadOfflineService } from '../../../_services/image-upload-offline.service';
import { ImageUploadService } from '../../../_services/image-upload.service';
import { LangUpdateService } from '../../../_services/lang-update.service';
import { NetworkStatusService } from '../../../_services/network-status.service';
import { SpinnerService } from '../../../_services/spinner.service';
import { SummaryDialogComponent } from '../../../inspection-data-management/summary-comment/summary-comment-dialog.component';
import { BROWSER_TYPE, INSPECTION_REPORT_STATUS_CODE, PLATFORM } from '../../../app.constants';
import { InspectionDataManageService } from '../../inspection-data-manage.service';
import { FormControl } from "@angular/forms";
import { InspectionReport } from "../../../_objects/inspection-report";
import { LocationStateService } from "../../../_services/location/location-state/location-state.service";
import { InspectionStateService } from "../../../_services/inspection/inspection-state.service";
import { LocationManagementService } from "../../../_services/location/location-management.service";
import { InspectionReportStateService } from "../../../_services/reports/inspection-report-state.service";
import { IconEnum } from "@evc/web-components";
import { InspectionImageStateService } from "../../../inspection-data-management/inspection-images-state.service";
import { EvcModalService } from '../../../_shared/services/modal/evc-modal.service';
import { UserPermissionsService } from '../../../_services/http-api/user-permissions.service';

@Component({
    selector: 'app-location-folder-tab',
    templateUrl: './location-folder-tab.component.html',
    styleUrls: ['./location-folder-tab.component.scss'],
    standalone: false
})
export class LocationFolderTabComponent implements OnInit, OnDestroy {

  @Input() subjectModel: SubjectModel;
  @Input() inspection: Inspection;
  @Input() tooltipPosition: string;
  @Input() height: number;
  @Input() isReadOnly: boolean;
  @Input() inspectionReport: InspectionReport;

  @Output() imageUpload = new EventEmitter<boolean>();
  @Output() updateInspection = new EventEmitter<Inspection>();

  filterLocation: SubjectLocation;
  filterTermLocations: SubjectLocation[] = [];
  isDataPlateSelected: boolean = false;
  canEditInspection: boolean = false;
  termLocationControl: FormControl;
  nestedTreeControl: NestedTreeControl<SubjectLocation>;
  nestedDataSource: MatTreeNestedDataSource<SubjectLocation>;
  locationIdentifier: string;
  selectedLocation: SubjectLocation;
  browserFlag = true;
  isDragOverLocation: SubjectLocation = null;
  isDragOverInspectionImage: InspectionImage;
  isDragOverRowLocationId: string;
  paddingValue = 30;
  toggleIconSize = 10;
  uncheckedBox = IconEnum.Square;
  checkedBox = IconEnum.SquareCheck;
  inspectionImages : InspectionImageListItem [] =[];

  private onDestroy$ = new Subject();

  hasNestedChild = (_: number, subjectLocationData: SubjectLocation) =>
    subjectLocationData.children && subjectLocationData.children.length > 0;

  private _getChildren = (node: SubjectLocation) => node.children;

  get isSelectedImage(): boolean {
    return this.inspDataManageService.selectImageId.length > 0;
  }

  get isDesktop() {
    return this.networkStatusService.isDesktop();
  }

  get isOffline() {
    return this.networkStatusService.isOffline;
  }

  constructor(
    private inspDataManageService: InspectionDataManageService,
    private imageUploadOnlineService: ImageUploadService,
    private imageUploadOfflineService: ImageUploadOfflineService,
    private snackBar: CommonSnackBarService,
    private utilsService: UtilsService,
    private oscSpinnerService: SpinnerService,
    private inspectionService: InspectionService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private networkStatusService: NetworkStatusService,
    private locationStateService: LocationStateService,
    private inspectionStateService: InspectionStateService,
    private locationManagementService: LocationManagementService,
    private inspectionReportStateService : InspectionReportStateService,
    private inspectionImageStateService : InspectionImageStateService,
    private evcModalService: EvcModalService,
    private userPermission: UserPermissionsService
  ) {
    this.langService.prepare(translate, this.onDestroy$);
    if (!this.tooltipPosition) {
      this.tooltipPosition = 'above';
    }
  }

  ngOnInit(): void {
    this.browserFlag = this.isChromeOrEdgeBrowser();
    this.userPermission.updateUserProperties(this.inspection);

    this.inspDataManageService
      .filterLocationObs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (loc) => {
          this.selectedLocation = loc;
        },
        error: (error) => {
          console.log(error);
        }
      });

    this.initLocations();
    this.initCanEditInspection();
    this.initInspectionImages();
  }

  private initLocations() {

    combineLatest([
      this.locationStateService.getFilterLocation(),
      this.locationStateService.getFilterTermLocations(),
      this.locationStateService.getIsDataPlateSelected(),
      this.inspectionReportStateService.getInspectionReport()
    ])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([filterLocation, filterTermLocations, isDataPlateSelected, inspectionReport]) => {
        this.filterLocation = filterLocation;
        this.filterTermLocations = filterTermLocations;
        this.isDataPlateSelected = isDataPlateSelected;
        this.inspectionReport = inspectionReport;
      });

    this.nestedDataSource = new MatTreeNestedDataSource();
    const allSubLoc = new SubjectLocation();
    allSubLoc.identifier = null;
    allSubLoc.name = this.translate.instant('locationFolderTab.all');
    allSubLoc.children = [];
    allSubLoc.inspectionFor = [];
    this.nestedDataSource.data = [allSubLoc].concat(this.subjectModel.locations);
    this.nestedTreeControl = new NestedTreeControl<SubjectLocation>(this._getChildren);
    this.nestedTreeControl.dataNodes = this.subjectModel.locations;
  }

  isSelectedLocationContainsImages(subjectLocationId): boolean {
    return this.inspectionImages.some(image =>image.subjectLocationId == subjectLocationId);
  }

  private initCanEditInspection():void {
    this.inspectionStateService.getCanEditInspection()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(canEdit => {
        this.canEditInspection = canEdit;
      });
  }

  private initInspectionImages() {
    this.inspectionImageStateService.inspectionImages$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(images => {
        this.inspectionImages = images;
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  folderOpen() {
    this.nestedTreeControl.expandAll();
  }

  folderClose() {
    this.nestedTreeControl.collapseAll();
  }

  isAllOpen(): boolean {
    if (this.nestedDataSource.data) {
      let isOpen = true;
      this.nestedDataSource.data.forEach((subjectLocation: SubjectLocation) => {
        if (subjectLocation.children.length !== 0 &&
          !this.isOpen(subjectLocation)) {
          isOpen = false;
        }
      });
      return isOpen;
    } else {
      return true;
    }
  }

  isOpen(subjectLocation: SubjectLocation): boolean {
    if (!this.nestedTreeControl.isExpanded(subjectLocation)) {
      return false;
    }
    if (subjectLocation.children.length > 0) {
      let isOpen = true;
      subjectLocation.children.forEach((children: SubjectLocation) => {
        if (!this.isOpen(children)) {
          isOpen = false;
        }
      });
      return isOpen;
    } else {
      return true;
    }
  }

  isClose(subjectLocation: SubjectLocation): boolean {
    if (this.nestedTreeControl.isExpanded(subjectLocation)) {
      return false;
    }
    if (subjectLocation.children.length > 0) {
      let isOpen = true;
      subjectLocation.children.forEach((children: SubjectLocation) => {
        if (!this.isClose(children)) {
          isOpen = false;
        }
      });
      return isOpen;
    } else {
      return true;
    }
  }

  onDragOver(event: DragEvent, location: SubjectLocation) {
    this.isDragOverLocation = location;
    event.dataTransfer.dropEffect = 'copy';
    event.stopPropagation();
    event.preventDefault();
  }

  onDragLeave(event: DragEvent) {
    this.isDragOverLocation = null;
    this.resetDragOverStatus();
  }

  async onDrop(event: DragEvent, location: SubjectLocation) {
    if (!this.isReadOnly) {
      this.isDragOverLocation = null;
      if (event.dataTransfer.items.length > 0) {
        this.locationIdentifier = location.identifier;
        event.stopPropagation();
        event.preventDefault();

        if (this.isOffline) {
          this.imageUploadOfflineService
            .uploadFiles(this.inspection.id, location.identifier, event.dataTransfer.items)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(result => {
              if (result) {
                this.imageUpload.emit(true);
              }
            });
        } else {
          from(this.extractFiles(event.dataTransfer.items))
            .subscribe(async files => await this.imageUploadOnlineService.uploadInspectionFiles(this.inspection.id, location.identifier, files));
        }
      } else if (event.dataTransfer.items.length === 0 && this.canMoveImage(location)) {
        this.inspDataManageService.moveImageLocationEmit(location);
        event.stopPropagation();
        event.preventDefault();
      }
    }
  }

  canMoveImage(location: SubjectLocation) {
    const selectImageIds = this.inspDataManageService.selectedImageIds;
    return location.identifier !== null
      && location.identifier !== this.selectedLocation?.identifier
      && selectImageIds.length !== 0
      && selectImageIds.every(img => img.locationId !== location.identifier);
  }

  callSelectLocation4FilterImages(location: SubjectLocation) {
    if (this.isSelectedNested(location)) {
      this.selectLocation(location);

      this.nestedTreeControl.collapseAll();
      this.nestedTreeControl.expand(this.selectedLocation);
      this.expandChildren(this.selectedLocation);
    }
  }

  private extractFiles(dtItemList: DataTransferItemList) {
    const systemEntries: FileSystemEntry[] = [];
    for (let i = 0; i < dtItemList.length; i++) {
      systemEntries.push(dtItemList[i].webkitGetAsEntry());
    }

    return extractFilesFromSystemEntries(systemEntries, []);
  }

  isSelectedNested(location: SubjectLocation): boolean {
    const level = this.getLevel(this.nestedDataSource.data, location);
    return level === 0 || location.children.length === 0;
  }

  resetDragOverStatus() {
    this.isDragOverInspectionImage = null;
    this.isDragOverRowLocationId = null;
  }
  private expandChildren(location: SubjectLocation): void {
    if (!location || location.children?.length < 1) {
      return;
    }

    location.children.forEach(child => this.nestedTreeControl.expand(child));
  }

  selectLocation(location: SubjectLocation) {
    this.inspDataManageService.filterLocationEmit(location);
    this.inspDataManageService.viewImagesEmit();
  }

  imageLocationMultipleUpdate(location: SubjectLocation) {
    this.inspDataManageService.moveImageLocationEmit(location);
  }

  openSectionSummaryDialog(subjectLocation: SubjectLocation) {
    const tempLocation = this.inspection.inspectionLocations.find((location) => location.identifier === subjectLocation.identifier) ??
      new InspectionLocation(subjectLocation.identifier, '');

    this.evcModalService
      .showModal(SummaryDialogComponent,
        {
          locationName: tempLocation.identifier,
          comment: tempLocation.comment
        })
      .afterClosed.pipe(take(1))
      .subscribe(updatedComment => {
        if (updatedComment !== undefined && tempLocation.comment !== updatedComment) {
          this.oscSpinnerService.show();

          let locations: InspectionLocation[] = this.inspection.inspectionLocations;
          const targetLoc = locations.find(loc => loc.identifier === subjectLocation.identifier);

          if (targetLoc) {
            targetLoc.comment = updatedComment;
          } else {
            const addLocations = [new InspectionLocation(subjectLocation.identifier, updatedComment)];
            locations = locations.concat(addLocations);
          }

          const updateData = new InspectionUpdateLocation(this.inspection.id, locations);

          this.inspectionService
            .updateInspectionLocations(updateData)
            .subscribe(
              response => this.inspection = response,
              error => {
                this.snackBar.open(this.translate.instant('locationFolderTab.openSectionSummaryDialogError'));
                console.log(error);
                this.oscSpinnerService.hide();
              },
              () => {
                this.updateInspection.emit(this.inspection);
                this.snackBar.open(this.translate.instant('locationFolderTab.openSectionSummaryDialogSuccessfully'));
                this.oscSpinnerService.hide();
              }
            );
        }
      });
  }

  selectedParentNode(subjectLocation: SubjectLocation) {
    if (this.nestedTreeControl.isExpanded(subjectLocation)) {
      return subjectLocation?.identifier === this.selectedLocation?.identifier;
    } else {
      return subjectLocation?.identifier === this.selectedLocation?.identifier
        || subjectLocation.children.some(child => child?.identifier === this.selectedLocation?.identifier);
    }
  }

  private isChromeOrEdgeBrowser(): boolean {
    const browserType = this.utilsService.getBrowserName();
    return browserType === BROWSER_TYPE.EDGE || browserType === BROWSER_TYPE.CHROME;
  }

  getLevel(data: Data, node: SubjectLocation) {
    const path = data.find(branch => {
      return this.nestedTreeControl
        .getDescendants(branch)
        .some(n => n.identifier === node.identifier);
    });

    return path ? this.getLevel(path.children, node) + 1 : 0;
  }

  getPaddingLeftValue(data: Data, node: SubjectLocation) {
    const level = this.getLevel(data, node);
    const value = (level * this.paddingValue) + (node.children.length > 0 ? 0 : this.toggleIconSize);

    return value + 'px';
  }

  isSelectedLocationForReport(location: SubjectLocation): boolean {
    return this.locationManagementService.isSelectedLocationForReport(location, this.inspectionReport);
  }

  selectLocation4Report(location: SubjectLocation, event : Event) {

    event.stopPropagation();

    if (location) {
      this.inspectionReport = this.locationManagementService.toggleLocationInReport(location, this.inspectionReport, this.inspectionImages);
      this.inspectionReportStateService.setInspectionReport(this.inspectionReport);
      this.locationManagementService.patchInspectionReportLocations(this.inspectionReport);
    }
  }

  public get displayReportLocationCheckbox(): boolean {
    return this.inspectionReport && this.inspectionReport.status !== INSPECTION_REPORT_STATUS_CODE.COMPLETED && this.canEditInspection;
  }

  get canDisplayEditSectionSummaryButton(): boolean {
    return this.userPermission.isAdminRole || this.userPermission.isInspectionOwner || this.networkStatusService.isOnPremise;
  }

}
